import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { leadService } from '../../../services/leadService';
import { eventService } from '../../../services/eventService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { ConnectionDetailModal } from '../../../components/app/connections/ConnectionDetailModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const EventConnections = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      try {
        const [eventData, leadsData] = await Promise.all([
          eventService.getEvent(id, user.id),
          leadService.getEventLeads(id, user.id)
        ]);
        setEvent(eventData);
        setLeads(leadsData);
      } catch (err) {
        console.error('Failed to load event connections:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user]);

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Company', 'Job Title', 'Connected On', 'Status'];
    const rows = leads.map(l => [
      l.full_name,
      l.email,
      l.phone || '',
      l.company_name || '',
      l.job_title || '',
      new Date(l.created_at).toISOString(),
      l.status
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(field => `"${(field || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event_connections_${event?.name || 'export'}.csv`;
    link.click();
  };

  const handleOpenLead = async (lead) => {
    setSelectedLead(lead);
    if (!lead.is_read) {
      try {
        const updated = await leadService.updateLead(lead.id, user.id, { is_read: true });
        setLeads(prev => prev.map(l => l.id === updated.id ? updated : l));
      } catch (err) {
        console.error('Failed to mark read');
      }
    }
  };

  const handleUpdateLead = (updatedLead) => {
    setLeads(prev => prev.map(l => l.id === updatedLead.id ? { ...l, ...updatedLead } : l));
    setSelectedLead(prev => ({ ...prev, ...updatedLead }));
  };

  const filteredLeads = leads.filter(l => {
    if (l.is_archived) return false;
    if (search) {
      const s = search.toLowerCase();
      if (
        !l.full_name.toLowerCase().includes(s) &&
        !l.email.toLowerCase().includes(s) &&
        !(l.company_name && l.company_name.toLowerCase().includes(s))
      ) {
        return false;
      }
    }
    return true;
  });

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!event) return <div className="text-center py-20">Event not found.</div>;

  return (
    <div className="max-w-6xl pb-12">
      {selectedLead && (
        <ConnectionDetailModal
          lead={selectedLead}
          user={user}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleUpdateLead}
        />
      )}

      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/events')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Events
        </button>
        <span className="mx-2">/</span>
        <button onClick={() => navigate(`/app/events/${id}`)} className="hover:text-slate-900">
          {event.name}
        </button>
        <span className="mx-2">/</span>
        <span className="text-slate-900">Connections</span>
      </div>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Event Connections</h1>
          <p className="mt-2 text-slate-600">Relationships generated from this event.</p>
        </div>
        {leads.length > 0 && (
          <Button variant="secondary" onClick={handleExportCSV}>
            <SafeIcon icon={FiIcons.FiDownload} className="mr-2" /> Export CSV
          </Button>
        )}
      </header>

      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative w-full sm:max-w-sm">
          <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Search event connections..." 
            value={search} 
            onChange={e => setSearch(e.target.value)} 
            className="pl-10 m-0" 
          />
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 border-dashed p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiIcons.FiUsers} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">No connections found</h3>
          <p className="text-slate-500 max-w-sm mx-auto">
            {leads.length === 0 
              ? 'No connections have been made through this Event yet.' 
              : 'Try adjusting your search terms.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeads.map(lead => (
            <div key={lead.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col hover:shadow-md transition-shadow relative">
              {!lead.is_read && <span className="absolute top-6 left-2 w-2 h-2 rounded-full bg-blue-500" title="New" />}
              <div className="flex justify-between items-start mb-3 pl-2">
                <div className="flex-1 pr-3">
                  <h3 className="font-bold text-lg text-slate-900 truncate flex items-center">
                    {lead.full_name}
                    {lead.is_important && <SafeIcon icon={FiIcons.FiStar} className="ml-1.5 text-amber-500 fill-current shrink-0" />}
                  </h3>
                  {(lead.job_title || lead.company_name) && (
                    <p className="text-sm text-slate-600 truncate">
                      {lead.job_title} {lead.company_name ? `at ${lead.company_name}` : ''}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="pl-2 flex flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">
                  {lead.status.replace('_', ' ')}
                </span>
                {lead.cards?.internal_name && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 truncate max-w-[150px]">
                    Card: {lead.cards.internal_name}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto pl-2">
                <div className="text-xs text-slate-500">
                  {new Date(lead.created_at).toLocaleDateString()}
                </div>
                <Button variant="secondary" size="sm" onClick={() => handleOpenLead(lead)}>
                  Manage
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};